/** Content sourced from https://dev.mosida.com/term-of-use */

const TermsOfUseContent = () => (
  <div className="legalDocumentContent">
    <h1>MOSIDA TERMS OF USE</h1>
    <h2>PLEASE READ THESE TERMS OF USE CAREFULLY.</h2>
    <p>
      If you disagree with any part of these Terms, please refrain from using our Site,
      facilities, or services. MOSIDA may update these Terms at any time and may condition
      continued use upon acceptance of revised Terms.
    </p>
    <p>
      The terms “MOSIDA,” “we,” “us,” or “our” refer to MOSIDA LLC, a Florida limited
      liability company, and its affiliates.
    </p>
    <p>
      The term “you” refers to any visitor, ticket purchaser, website user, or participant.
    </p>
    <p>
      By accessing or using mosida.com, any MOSIDA mobile application, interactive system,
      AI station, onsite technology, ticketing platform, or physical exhibition space
      (collectively, the “Site” or “Experience”), you agree to comply with these Terms.
    </p>

    <h2>1. The Site &amp; Experience</h2>
    <h3>1.1 Eligibility</h3>
    <p>
      You must be at least 16 years old to use MOSIDA’s online services independently.
      Visitors under 16 must be accompanied by a parent or legal guardian.
    </p>
    <p>
      Parents or guardians assume full responsibility for minors in their care, including
      participation in interactive exhibits and digital content creation.
    </p>
    <h3>1.2 Permission to Use</h3>
    <p>
      MOSIDA grants you a limited, non-exclusive, revocable license to access and use the
      Site and Experience subject to these Terms. Your use is at your own risk.
    </p>
    <h3>1.3 Availability</h3>
    <p>
      MOSIDA reserves the right to modify, suspend, update, or discontinue any portion of
      the Site, Experience, or exhibits at any time without notice.
    </p>

    <h2>2. Privacy &amp; Communications</h2>
    <h3>2.1 Privacy Policy</h3>
    <p>
      Your use of MOSIDA is governed by our Privacy Policy, incorporated herein by
      reference.
    </p>
    <h3>2.2 User Information</h3>
    <p>
      You agree to provide accurate and complete information when purchasing tickets,
      creating accounts, submitting content, or using AI systems.
    </p>
    <p>
      You may not impersonate another individual or provide false identity information.
    </p>
    <h3>2.3 Communications</h3>
    <p>
      By purchasing tickets, submitting information, or participating in MOSIDA programs,
      you consent to receive communications via email, SMS, or other methods related to:
    </p>
    <ul>
      <li>Ticket confirmations</li>
      <li>Event updates</li>
      <li>Promotions</li>
      <li>Special exhibitions</li>
      <li>Administrative notices</li>
    </ul>
    <p>You may unsubscribe from marketing communications at any time.</p>

    <h2>3. Interactive Content &amp; AI-Generated Works</h2>
    <h3>3.1 Definitions</h3>
    <p>
      “Content” includes text, images, video, audio, biometric data, motion data, AI
      outputs, digital art, projections, and all other media.
    </p>
    <p>
      “Your Content” means content you submit, upload, speak, photograph, record, or create
      through MOSIDA systems.
    </p>
    <p>
      “MOSIDA Content” means all content created or owned by MOSIDA, including AI systems,
      projections, software, exhibits, digital environments, and artwork.
    </p>
    <h3>3.2 Ownership of Your Content</h3>
    <p>
      You retain ownership of original content you submit. However, by participating in
      MOSIDA experiences, you grant MOSIDA a worldwide, perpetual, royalty-free,
      irrevocable, transferable, sublicensable license to use, reproduce, modify, transform
      into AI-generated works, display publicly, distribute, create derivative works, use
      in advertising and promotion, and use in future exhibitions.
    </p>
    <p>
      This includes content integrated into AI-generated videos, digital murals, projection
      art, social media promotion, printed merchandise, and future MOSIDA exhibits.
    </p>
    <p>You waive any moral rights or claims of attribution.</p>
    <h3>3.3 AI-Generated Content</h3>
    <p>
      MOSIDA utilizes artificial intelligence systems to generate artworks incorporating
      visitor input (e.g., selfies, text, voice, gestures, emotions).
    </p>
    <p>You acknowledge that:</p>
    <ul>
      <li>
        AI outputs may transform, stylize, distort, animate, or reinterpret your likeness.
      </li>
      <li>
        AI-generated works may include your image integrated with other visitors.
      </li>
      <li>
        Outputs are artistic interpretations and not guaranteed to represent you
        accurately.
      </li>
      <li>
        MOSIDA retains ownership of AI-generated outputs created within its systems.
      </li>
    </ul>
    <h3>3.4 Photography, Recording &amp; Likeness Release</h3>
    <p>
      By entering MOSIDA, you consent to being photographed, recorded, scanned, or digitally
      captured.
    </p>
    <p>
      You grant MOSIDA the right to use your name, image, likeness, voice, and digital
      representation for promotional purposes, advertising, social media, press materials,
      exhibition documentation, and future immersive displays.
    </p>
    <p>
      If you do not wish to appear in promotional materials, you must notify staff in
      writing prior to participation.
    </p>
    <h3>3.5 MOSIDA Intellectual Property</h3>
    <p>
      All MOSIDA systems, code, software, installations, visual interfaces, interactive
      mechanics, and brand elements — including the slogan “The Masterpiece Starts With
      You.” — are protected by intellectual property laws.
    </p>
    <p>You may not:</p>
    <ul>
      <li>Copy or reproduce installations</li>
      <li>Reverse engineer software</li>
      <li>Record or distribute full exhibits commercially</li>
      <li>Create derivative commercial works</li>
      <li>Use MOSIDA trademarks without written consent</li>
    </ul>

    <h2>4. Prohibited Conduct</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Damage installations or equipment</li>
      <li>Interfere with interactive systems</li>
      <li>Upload unlawful, obscene, or infringing content</li>
      <li>Harass staff or other visitors</li>
      <li>Use MOSIDA systems for unauthorized commercial purposes</li>
      <li>Attempt to hack, manipulate, or exploit AI systems</li>
    </ul>
    <p>Violation may result in removal without refund.</p>

    <h2>5. Health &amp; Safety; Assumption of Risk</h2>
    <p>
      MOSIDA is an immersive, technology-driven environment that may include low lighting,
      moving projections, stairs or uneven flooring, sound effects, visual stimulation, and
      motion tracking systems.
    </p>
    <p>
      By entering, you acknowledge and voluntarily assume all risks associated with slips,
      falls, or physical injury, exposure to viruses or infectious diseases, sensory
      discomfort, and equipment malfunction.
    </p>
    <p>
      You agree to release MOSIDA from liability except in cases of gross negligence or
      willful misconduct.
    </p>

    <h2>6. Ticket Purchase Terms</h2>
    <h3>6.1 No Refunds</h3>
    <p>All ticket sales are final unless otherwise required by law.</p>
    <h3>6.2 Assumption of Risk</h3>
    <p>
      By purchasing or redeeming a ticket, you agree to assume all risks of participation
      for yourself and minors in your care.
    </p>
    <h3>6.3 Removal</h3>
    <p>
      Failure to comply with MOSIDA’s Code of Conduct may result in removal without refund.
    </p>
    <h3>6.4 Communications</h3>
    <p>
      Ticket purchasers consent to email and SMS correspondence regarding their visit.
    </p>

    <h2>7. Warranty Disclaimer</h2>
    <p>
      THE SITE AND EXPERIENCE ARE PROVIDED “AS IS” AND “AS AVAILABLE.” MOSIDA MAKES NO
      WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR
      A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
    </p>
    <p>
      MOSIDA DOES NOT GUARANTEE continuous operation of digital systems, error-free AI
      outputs, data retention, or availability of specific exhibits.
    </p>

    <h2>8. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, MOSIDA shall not be liable for indirect,
      incidental, special, or consequential damages. If liability cannot be excluded,
      MOSIDA’s maximum liability shall not exceed the amount paid for your ticket.
    </p>

    <h2>10. Dispute Resolution</h2>
    <p>
      <strong>Governing Law.</strong> These Terms are governed by the laws of the State of
      Florida.
    </p>
    <p>
      <strong>Arbitration.</strong> All disputes shall be resolved by binding arbitration in
      Miami-Dade County, Florida, under the Federal Arbitration Act. Class actions are
      waived. Claims must be brought within one (1) year.
    </p>

    <h2>11. Miscellaneous</h2>
    <p>
      These Terms constitute the entire agreement. Failure to enforce a provision is not a
      waiver. If a provision is invalid, the remainder remains enforceable. MOSIDA may
      assign these Terms without restriction.
    </p>

    <h2>MOSIDA TICKET RELEASE &amp; WAIVER</h2>
    <p>BY PURCHASING OR REDEEMING A TICKET TO MOSIDA, YOU AGREE:</p>
    <ul>
      <li>You voluntarily assume all risks.</li>
      <li>You release MOSIDA from claims related to your visit.</li>
      <li>You understand interactive exhibits involve movement and technology.</li>
      <li>You understand your likeness may appear in digital installations.</li>
      <li>
        You consent to being recorded and digitally integrated into artworks.
      </li>
      <li>
        You waive and release MOSIDA from liability for injury, illness, loss, or damage
        except where prohibited by law.
      </li>
    </ul>
  </div>
);

export default TermsOfUseContent;
